import React from "react";
import { Form, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { Grid, GridItem } from "@libertify/ui";
import { FormLabel } from "../../FormLabel";
import { PillSegmented } from "../PillSegmented";
import type { SidePanelPosition } from "../types";
import { LayoutPreview } from "./LayoutPreview";
import {
  SectionHeading,
  SectionSubtitle,
  ControlsColumn,
  FieldBlock,
} from "./GeneralSettingsConfigCard.styles";

const KEY = "createExperience.generalSettings";

interface LayoutSectionProps {
  /** Whether the AI Chat add-on is enabled — drives the preview's read-only
   *  chat indicator. Sourced differently per card (draft context vs. the
   *  edit page's add-ons list), so it's passed in rather than derived here. */
  hasChatAddon: boolean;
}

/**
 * Layout section (sidebar position + live preview) — shared between the
 * create-flow config card and the edit card, which render it identically.
 * Must be rendered inside a Form bound to `GeneralSettingsFormValues` (or a
 * superset of it).
 */
export const LayoutSection: React.FC<LayoutSectionProps> = ({
  hasChatAddon,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  // API `GeneralFunctionalSettings.side_panel_position` ("Left" | "Right").
  const sidePanel: SidePanelPosition =
    Form.useWatch(["functional_settings", "side_panel_position"]) ?? "Right";

  const sidePanelOptions = [
    { value: "Left", label: t(`${KEY}.layout.sidePanel.left`) },
    { value: "Right", label: t(`${KEY}.layout.sidePanel.right`) },
  ];

  return (
    <>
      <SectionHeading>{t(`${KEY}.layout.heading`)}</SectionHeading>
      <SectionSubtitle $token={token}>
        {t(`${KEY}.layout.subtitle`)}
      </SectionSubtitle>
      <Grid columns={12} gap="xl" alignItems="center">
        <GridItem span={12} mdSpan={7}>
          <LayoutPreview
            sidePanel={sidePanel}
            hasChatAddon={hasChatAddon}
            documentLabel={t(`${KEY}.layout.document`)}
            knowledgeHubLabel={t(`${KEY}.layout.knowledgeHub`)}
          />
        </GridItem>
        <GridItem span={12} mdSpan={5}>
          <ControlsColumn>
            <FieldBlock>
              <FormLabel label={t(`${KEY}.layout.sidePanel.label`)} />
              <Form.Item
                name={["functional_settings", "side_panel_position"]}
                noStyle
              >
                <PillSegmented
                  $token={token}
                  $width="50%"
                  block
                  options={sidePanelOptions}
                />
              </Form.Item>
            </FieldBlock>
          </ControlsColumn>
        </GridItem>
      </Grid>
    </>
  );
};

export default LayoutSection;
